import store from '../store.js'
import { getGraphAccessToken, getSpAccessToken, getSpGraphAccessToken } from '../../lib/msal.js'
import { fromByteArray } from 'base64-js'
import { setSelectedItem } from '../selected/actions.js'
import {
  beginProgressDialog,
  updateProgressDialog,
  endProgressDialog
} from '../progress/actions.js'
import {
  absoluteUrl,
  formatResourceString,
  isOfficeZipFile,
  getFileExtension,
  getItemUrl,
  getItemRoot,
  wait
} from '../../util'
import {
  addFileAttachmentFromBase64,
  clearCustomXml
} from '../../lib/office.js'
import {
  getDocumentLibraryFiles,
  deleteDocumentLibraryItem,
  readSharePointFile,
  readSharePointFileAsPdf,
  writeSharePointFileNextToOriginal,
  renameDocumentLibraryItem,
  searchDocumentLibrary
} from '../../lib/sharepoint/index.js'

import { ERROR_ACTION } from '../error/actions.js'

const ERROR_TYPE_FILE_READ = 'ERROR_TYPE_FILE_READ'
const ERROR_TYPE_DOC_LIB_GET = 'ERROR_TYPE_DOC_LIB_GET'
const ERROR_TYPE_FILE_ATTACH = 'ERROR_TYPE_FILE_ATTACH'
const ERROR_TYPE_FILE_SEARCH = 'ERROR_TYPE_FILE_SEARCH'
const ERROR_TYPE_FOLDER_CREATED_ADD = 'ERROR_TYPE_FOLDER_CREATED_ADD'
const ERROR_TYPE_FILE_RENAME = 'ERROR_TYPE_FILE_RENAME'
const ERROR_TYPE_FILE_RENAME_INVALID = 'ERROR_TYPE_FILE_RENAME_INVALID'
const ERROR_TYPE_FILE_RENAME_LOCKED = 'ERROR_TYPE_FILE_RENAME_LOCKED'
const ERROR_TYPE_FILE_LOCKED = 'ERROR_TYPE_FILE_LOCKED'
const ERROR_TYPE_FILE_DELETE = 'ERROR_TYPE_FILE_DELETE'
const ERROR_TYPE_FOLDER_NOT_EMPTY = 'ERROR_TYPE_FOLDER_NOT_EMPTY'
const ERROR_TYPE_FILE_UPLOAD = 'ERROR_TYPE_FILE_UPLOAD'
const ERROR_TYPE_FILE_PDF_CONVERSION = 'ERROR_TYPE_FILE_PDF_CONVERSION'

export const FILES_SET_SELECTED_FOLDER_ACTION = 'FILES_SET_SELECTED_FOLDER_ACTION'
export const FILES_SET_SELECTED_FILE_ACTION = 'FILES_SET_SELECTED_FILE_ACTION'
export const FILES_SET_SELECTED_FILES_ACTION = 'FILES_SET_SELECTED_FILES_ACTION'
export const FILES_RESET_SELECTED_FILE_ACTION = 'FILES_RESET_SELECTED_FILE_ACTION'
export const FILES_GET_ROOT_FOLDER_ACTION = 'FILES_GET_ROOT_FOLDER_ACTION'
export const FILES_GET_SUB_FOLDER_ACTION = 'FILES_GET_SUB_FOLDER_ACTION'
export const FILES_SET_SEARCH_VALUE_ACTION = 'FILES_SET_SEARCH_VALUE_ACTION'
export const FILES_GOT_SEARCH_RESULTS_ACTION = 'FILES_GOT_SEARCH_RESULTS_ACTION'
export const FILES_BEGIN_SEARCH_ACTION = 'FILES_BEGIN_SEARCH_ACTION'
export const FILES_END_SEARCH_ACTION = 'FILES_END_SEARCH_ACTION'
export const FILES_RESET_SEARCH_ACTION = 'FILES_RESET_SEARCH_ACTION'

let latestFileSearchRequest = 0

function isSharePointLockedError (err) {
  return err?.code === 'ITEM_LOCKED' || Number(err?.status) === 423
}

function resolveLockedFileErrorType (err, fallbackType) {
  return isSharePointLockedError(err) ? ERROR_TYPE_FILE_LOCKED : fallbackType
}

function resolveLockedFileErrorMessage (err) {
  if (!isSharePointLockedError(err)) {
    return err?.message
  }

  const { strings } = store.getState().locale || {}
  const get = (key, fallback) => {
    const value = strings?.[key]
    return typeof value === 'string' && value.length > 0 ? value : fallback
  }

  const genericHint = get(
    'FILE_LOCK_HINT_GENERIC',
    'The file is locked or currently in use. Close it and try again in a moment.'
  )

  const lockedBy = err?.lockDiagnostics?.lockedByUser
  if (lockedBy && (lockedBy.Email || lockedBy.Title)) {
    const who = lockedBy.Email || lockedBy.Title
    return formatResourceString(
      get(
        'FILE_LOCK_HINT_LOCKED_BY_USER',
        'Locked by %1. Ask that user to close the document, then try again.'
      ),
      who
    )
  }

  const checkedOutBy = err?.lockDiagnostics?.checkOutByUser
  if (checkedOutBy && (checkedOutBy.Email || checkedOutBy.Title)) {
    const who = checkedOutBy.Email || checkedOutBy.Title
    return formatResourceString(
      get(
        'FILE_LOCK_HINT_CHECKED_OUT_BY_USER',
        'Checked out by %1. Ask that user to check in or close the document, then try again.'
      ),
      who
    )
  }

  return genericHint
}

export function setSelectedFolder (folder, options = {}) {
  return {
    type: FILES_SET_SELECTED_FOLDER_ACTION,
    folder,
    toggleExpand: options?.toggleExpand !== false
  }
}

export function setSelectedFile (file) {
  return {
    type: FILES_SET_SELECTED_FILE_ACTION,
    file
  }
}

export function setSelectedFiles (files) {
  return {
    type: FILES_SET_SELECTED_FILES_ACTION,
    files: Array.isArray(files) ? files : []
  }
}

export function resetSelectedFile () {
  return {
    type: FILES_RESET_SELECTED_FILE_ACTION
  }
}

export function setSearchValue (searchValue) {
  return {
    type: FILES_SET_SEARCH_VALUE_ACTION,
    searchValue
  }
}

export function resetSearch () {
  return {
    type: FILES_RESET_SEARCH_ACTION
  }
}

/**
 * Get files and folders from templates
 */
export function getTemplatesRootFolder () {
  const { login, locale } = store.getState()
  const item = {
    type: 'templates',
    id: 0,
    url: `${login.userData.sc_root}_admin`,
    name: locale.strings.templates_root_node
  }
  return function (dispatch) {
    dispatch(setSelectedItem(item))
    dispatch(getRootFolder(item))
  }
}

/**
 * Get files and folders for a root folder item
 */
export function getRootFolder (siteItem) {
  return async function (dispatch) {
    const siteUrl = getItemUrl(siteItem)
    const path = getItemRoot(siteItem)
    try {
      dispatch({
        type: FILES_GET_ROOT_FOLDER_ACTION,
        siteItem,
        path,
        items: await getFilesHelper({ siteUrl, path })
      })
    } catch (err) {
      dispatch(documentLibraryError(err))
    }
  }
}

/**
 * Get files and folders from a sub folder
 */
export function getSubFolder (item, folderItem) {
  return async function (dispatch) {
    const siteUrl = getItemUrl(item)
    const { path, id } = folderItem
    try {
      dispatch({
        type: FILES_GET_SUB_FOLDER_ACTION,
        item,
        parent: folderItem,
        items: await getFilesHelper({ siteUrl, path, id })
      })
    } catch (err) {
      dispatch(documentLibraryError(err))
    }
  }
}

async function getFilesHelper ({ siteUrl, path, id }) {
  const { userData } = store.getState().login
  const spToken = await getSpAccessToken(userData)
  const result = await getDocumentLibraryFiles({
    siteUrl,
    path,
    id
  }, spToken)

  const folders = result.Folders.results.filter(folder => {
    return folder.Name !== 'Forms'
  }).map(folder => {
    return {
      type: 'folder',
      children: [],
      id: folder.UniqueId,
      name: folder.Name,
      title: folder.Title,
      itemCount: folder.ItemCount,
      created: folder.TimeCreated,
      modified: folder.TimeLastModified,
      path: `${path}/${folder.Name}`
    }
  }).sort(compareFilenames)

  const files = result.Files.results.filter(file => {
    const length = parseInt(file.Length, 10)
    return !isNaN(length) && length > 0
  }).map(file => {
    return {
      type: 'file',
      id: file.UniqueId,
      name: file.Name,
      title: file.Title,
      created: file.TimeCreated,
      modified: file.TimeLastModified,
      path,
      uri: absoluteUrl(userData.sc_root, file.ServerRelativeUrl),
      serverRelativeUrl: file.ServerRelativeUrl
    }
  }).sort(compareFilenames)

  return folders.concat(files)
}

/**
 * Reads a file from SharePoint and attaches it to an email.
 * Can only be called during compose mode in Outlook.
 */
export function attachFile (selectedFileNode) {
  return async function (dispatch) {
    const { uri, name } = selectedFileNode
    const { userData } = store.getState().login

    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_ATTACH_FILE',
        'PROGRESS_STATUS_READ_SHAREPOINT_FILE'
      )
    )

    let data = null

    try {
      const spToken = await getSpAccessToken(userData)
      data = await readSharePointFile(uri, spToken)
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_FILE_READ,
        errorMessage: err.message
      })
    }

    dispatch(updateProgressDialog('PROGRESS_STATUS_ATTACH_FILE'))

    if (isOfficeZipFile(name)) {
      console.info('clearing custom xml from', name)
      try {
        data = await clearCustomXml(data)
      } catch (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_ATTACH,
          errorMessage: err.message
        })
      }
    }

    const base64 = fromByteArray(data)
    addFileAttachmentFromBase64(base64, name, (err, id) => {
      if (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_ATTACH,
          errorMessage: err.message
        })
      } else {
        setTimeout(() => dispatch(endProgressDialog()), 1000)
        console.info('successfully added file attachment', uri, name, id)
      }
    })
  }
}

/**
 * Reads multiple files from SharePoint and attaches them to an email.
 * Can only be called during compose mode in Outlook.
 */
export function attachFiles (selectedFileNodes) {
  return async function (dispatch) {
    const nodes = Array.isArray(selectedFileNodes)
      ? selectedFileNodes.filter((node) => node?.type === 'file' && node?.uri && node?.name)
      : []

    if (nodes.length === 0) {
      return
    }

    if (nodes.length === 1) {
      return dispatch(attachFile(nodes[0]))
    }

    const { userData } = store.getState().login

    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_ATTACH_FILE',
        'PROGRESS_STATUS_READ_SHAREPOINT_FILE'
      )
    )

    try {
      const spToken = await getSpAccessToken(userData)

      for (const node of nodes) {
        const { uri, name } = node
        let data = await readSharePointFile(uri, spToken)

        if (isOfficeZipFile(name)) {
          data = await clearCustomXml(data)
        }

        dispatch(updateProgressDialog('PROGRESS_STATUS_ATTACH_FILE'))
        await attachBase64Promise(fromByteArray(data), name)
        dispatch(updateProgressDialog('PROGRESS_STATUS_READ_SHAREPOINT_FILE'))
      }

      setTimeout(() => dispatch(endProgressDialog()), 1000)
      console.info('successfully added file attachments', nodes.map(n => n.name))
    } catch (err) {
      dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_FILE_ATTACH,
        errorMessage: err.message
      })
    }
  }
}

function attachBase64Promise (base64, name) {
  return new Promise((resolve, reject) => {
    addFileAttachmentFromBase64(base64, name, (err, id) => {
      if (err) {
        reject(err)
      } else {
        resolve(id)
      }
    })
  })
}

/**
 * Reads a file from SharePoint as PDF and attaches it to an email.
 * Can only be called during compose mode in Outlook.
 */
export function attachFileAsPdf (selectedFileNode) {
  return async function (dispatch) {
    const { uri, name } = selectedFileNode
    const { userData } = store.getState().login
    const baseName = getPdfAttachmentName(name)

    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_ATTACH_FILE',
        'PROGRESS_STATUS_READ_SHAREPOINT_FILE'
      )
    )

    let data = null

    try {
      const spGraphToken = await getSpGraphAccessToken(userData)
      data = await readSharePointFileAsPdf(uri, spGraphToken)
    } catch (err) {
      return dispatch(getPdfConversionErrorAction(err))
    }

    dispatch(updateProgressDialog('PROGRESS_STATUS_ATTACH_FILE'))

    const base64 = fromByteArray(data)
    addFileAttachmentFromBase64(base64, baseName, (err, id) => {
      if (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_ATTACH,
          errorMessage: err.message
        })
      } else {
        setTimeout(() => dispatch(endProgressDialog()), 1000)
        console.info('successfully added pdf file attachment', uri, baseName, id)
      }
    })
  }
}

/**
 * Converts a file to PDF, saves the converted file in the same SharePoint folder,
 * and then attaches it to the current compose email.
 */
export function attachFileAsPdfAndSaveCopy (selectedFileNode) {
  return async function (dispatch) {
    const { uri, name } = selectedFileNode
    const { userData } = store.getState().login
    const pdfName = getPdfAttachmentName(name)

    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_ATTACH_FILE',
        'PROGRESS_STATUS_READ_SHAREPOINT_FILE'
      )
    )

    let data = null

    try {
      const spGraphToken = await getSpGraphAccessToken(userData)
      data = await readSharePointFileAsPdf(uri, spGraphToken)
    } catch (err) {
      return dispatch(getPdfConversionErrorAction(err))
    }

    try {
      dispatch(updateProgressDialog('PROGRESS_STATUS_UPLOAD_FILE'))
      const spToken = await getSpAccessToken(userData)
      const savedPdf = await writeSharePointFileNextToOriginal({
        uri,
        fileName: pdfName,
        fileData: data
      }, spToken)
      await waitForSharePointFileReadable(savedPdf.absoluteUrl, spToken)

      const siteItem = store.getState().selected.current
      const selectedNode = store.getState().files.selectedFileNode
      if (siteItem) {
        if (selectedNode?.type === 'folder') {
          await dispatch(getSubFolder(siteItem, selectedNode))
        } else {
          await dispatch(getRootFolder(siteItem))
        }
      }
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: resolveLockedFileErrorType(err, ERROR_TYPE_FILE_UPLOAD),
        errorMessage: resolveLockedFileErrorMessage(err)
      })
    }

    dispatch(updateProgressDialog('PROGRESS_STATUS_ATTACH_FILE'))

    const base64 = fromByteArray(data)
    addFileAttachmentFromBase64(base64, pdfName, (err, id) => {
      if (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_ATTACH,
          errorMessage: err.message
        })
      } else {
        setTimeout(() => dispatch(endProgressDialog()), 1000)
        console.info('successfully saved and attached pdf file', uri, pdfName, id)
      }
    })
  }
}

/**
 * Converts a file to PDF and saves the converted file in the same SharePoint folder.
 */
export function saveFileAsPdfCopy (selectedFileNode) {
  return async function (dispatch) {
    const { uri, name } = selectedFileNode
    const { userData } = store.getState().login
    const pdfName = getPdfAttachmentName(name)

    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_UPLOAD_FILE',
        'PROGRESS_STATUS_READ_SHAREPOINT_FILE'
      )
    )

    let data = null

    try {
      const spGraphToken = await getSpGraphAccessToken(userData)
      data = await readSharePointFileAsPdf(uri, spGraphToken)
    } catch (err) {
      return dispatch(getPdfConversionErrorAction(err))
    }

    try {
      dispatch(updateProgressDialog('PROGRESS_STATUS_UPLOAD_FILE'))
      const spToken = await getSpAccessToken(userData)
      const savedPdf = await writeSharePointFileNextToOriginal({
        uri,
        fileName: pdfName,
        fileData: data
      }, spToken)
      await waitForSharePointFileReadable(savedPdf.absoluteUrl, spToken)

      const siteItem = store.getState().selected.current
      const selectedNode = store.getState().files.selectedFileNode
      if (siteItem) {
        if (selectedNode?.type === 'folder') {
          await dispatch(getSubFolder(siteItem, selectedNode))
        } else {
          await dispatch(getRootFolder(siteItem))
        }
      }
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: resolveLockedFileErrorType(err, ERROR_TYPE_FILE_UPLOAD),
        errorMessage: resolveLockedFileErrorMessage(err)
      })
    }

    setTimeout(() => dispatch(endProgressDialog()), 1000)
    console.info('successfully saved pdf copy', uri, pdfName)
  }
}

async function waitForSharePointFileReadable (absoluteUrl, spToken) {
  if (!absoluteUrl) {
    return
  }

  const maxAttempts = 6
  const retryDelayMs = 500

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await ensureSharePointFileExists(absoluteUrl, spToken)
      return
    } catch (err) {
      if (attempt === maxAttempts) {
        console.info('pdf copy readiness check timed out, continuing refresh', absoluteUrl)
        return
      }
      await wait(retryDelayMs)
    }
  }
}

async function ensureSharePointFileExists (absoluteUrl, spToken) {
  const parsed = new URL(absoluteUrl)
  const siteCollection = parsed.pathname.split('/').slice(0, 3).join('/')
  const serverRelativeUrl = parsed.pathname.replace(/'/g, "''")
  const endpoint = `${parsed.origin}${siteCollection}/_api/web/GetFileByServerRelativeUrl('${serverRelativeUrl}')?$select=Exists`

  const res = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${spToken}`,
      Accept: 'application/json;odata=verbose',
      'Content-Type': 'application/json;odata=verbose'
    }
  })

  if (!res.ok) {
    throw new Error(`ensureSharePointFileExists() failed (${res.status})`)
  }
}

function getPdfConversionErrorAction (err) {
  if (Number(err?.status) === 406) {
    return {
      type: ERROR_ACTION,
      errorType: ERROR_TYPE_FILE_PDF_CONVERSION
    }
  }

  return {
    type: ERROR_ACTION,
    errorType: ERROR_TYPE_FILE_READ,
    errorMessage: err?.message
  }
}

export function createFolder (siteItem, item, name) {
  function getSiteItemUrl (siteItem) {
    switch (siteItem.type) {
      case 'contact':
      case 'project':
      case 'department':
        return `${(new URL(siteItem.url)).pathname}`
      case 'category':
        return `${(new URL(siteItem.collection_url)).pathname}`
    }
    return ''
  }

  return async function (dispatch) {
    try {
      const userData = store.getState().login.userData
      const token = await getSpAccessToken(userData)
      const url = getItemUrl(siteItem)
      const relativeUrl = `${getSiteItemUrl(siteItem)}/${item.path}`

      const options = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json;odata=verbose'
        },
        body: JSON.stringify({
          __metadata: {
            type: 'SP.Folder'
          },
          ServerRelativeUrl: `${relativeUrl}/${name}`
        })
      }

      const response = await fetch(`${url}/_api/web/folders`, options)
      if (!response.ok) {
        throw new Error('ERROR_TYPE_FOLDER_CREATED_ADD')
      }

      await dispatch(getSubFolder(siteItem, item))

      const treeId = `${siteItem.type}:${siteItem.id}`
      const treeItems = store.getState().files.trees?.[treeId]?.items || []
      const createdFolderPath = `${item.path}/${name}`
      const createdFolder = findTreeFolderByPath(treeItems, createdFolderPath)
      if (createdFolder) {
        dispatch(setSelectedFolder(createdFolder))
      }
    } catch (e) {
      dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_FOLDER_CREATED_ADD
      })
    }
  }
}

export function renameItem (siteItem, item, newName) {
  return async function (dispatch) {
    const trimmedName = String(newName || '').trim()
    if (!siteItem || !item || trimmedName.length === 0) {
      return
    }

    try {
      const userData = store.getState().login.userData
      const token = await getSpAccessToken(userData)
      const url = getItemUrl(siteItem)
      await renameDocumentLibraryItem({
        siteUrl: url,
        itemType: item.type,
        itemId: item.id,
        newName: trimmedName
      }, token)

      await refreshFileViewAfterMutation(dispatch, siteItem, item)
      // SharePoint can lag briefly after rename, especially for folders.
      setTimeout(() => {
        refreshFileViewAfterMutation(dispatch, siteItem, item)
      }, 250)
      setTimeout(() => {
        refreshFileViewAfterMutation(dispatch, siteItem, item)
      }, 800)
    } catch (err) {
      const invalidOrConflictName = err.code === 'INVALID_OR_CONFLICT_NAME' || err.status === 400 || err.status === 409
      const lockedItem = err.code === 'ITEM_LOCKED' || err.status === 423
      const { strings } = store.getState().locale || {}
      const lockHint = lockedItem ? buildRenameLockHint(err.lockDiagnostics, strings) : ''

      if (lockedItem) {
        console.warn('renameItem: SharePoint returned 423 (locked/in use)', {
          siteItemType: siteItem.type,
          itemType: item.type,
          itemId: item.id,
          itemName: item.name,
          attemptedName: trimmedName,
          lockDiagnostics: err.lockDiagnostics || null
        })
      }

      dispatch({
        type: ERROR_ACTION,
        errorType: invalidOrConflictName
          ? ERROR_TYPE_FILE_RENAME_INVALID
          : (lockedItem ? ERROR_TYPE_FILE_RENAME_LOCKED : ERROR_TYPE_FILE_RENAME),
        errorMessage: lockHint || err.message
      })
    }
  }
}

export function deleteItem (siteItem, item, options = {}) {
  return async function (dispatch) {
    if (!siteItem || !item || (item.type !== 'file' && item.type !== 'folder')) {
      return
    }

    try {
      const permanentDelete = !!options?.permanentDelete
      const userData = store.getState().login.userData
      const token = await getSpAccessToken(userData)
      const url = getItemUrl(siteItem)
      await deleteDocumentLibraryItem({
        siteUrl: url,
        itemType: item.type,
        itemId: item.id,
        permanentDelete
      }, token)

      const selectedNode = store.getState().files.selectedFileNode
      if (selectedNode?.type === 'file' && selectedNode.id === item.id) {
        dispatch(resetSelectedFile())
      }

      const refresh = () => refreshFileViewAfterMutation(dispatch, siteItem, item)

      // SharePoint can lag for larger file deletes, so retry refresh a few times.
      await refresh()
      setTimeout(refresh, 300)
      setTimeout(refresh, 900)
      setTimeout(refresh, 2000)
    } catch (err) {
      const folderDeleteError = item.type === 'folder'
      const lockHint = folderDeleteError ? buildDeleteLockHint(err.lockDiagnostics, store.getState().locale?.strings) : ''
      const fallbackDeleteHint = folderDeleteError
        ? (store.getState().locale?.strings?.DELETE_HINT_FOLDER_NOT_EMPTY ||
          'Cannot delete this folder right now. Try again in a moment.')
        : ''
      dispatch({
        type: ERROR_ACTION,
        errorType: folderDeleteError ? ERROR_TYPE_FOLDER_NOT_EMPTY : ERROR_TYPE_FILE_DELETE,
        errorMessage: lockHint || fallbackDeleteHint || err.message
      })
    }
  }
}

export function deleteFile (siteItem, fileItem) {
  return deleteItem(siteItem, fileItem)
}

export function deleteItemPermanently (siteItem, item) {
  return deleteItem(siteItem, item, { permanentDelete: true })
}

/**
 * Search files in currently selected context.
 */
export function searchFiles (siteItem, query) {
  return async function (dispatch) {
    const requestId = ++latestFileSearchRequest
    const { userData } = store.getState().login
    const scRoot = userData.sc_root

    const siteUrl = getItemUrl(siteItem)
    const serverRelativeUrl = getServerRelativeUrl(siteItem)
    console.info('searching files in', { siteItem, siteUrl, serverRelativeUrl, query })

    dispatch({ type: FILES_BEGIN_SEARCH_ACTION })

    function onResults (results) {
      if (requestId !== latestFileSearchRequest) {
        return
      }
      results = results.filter(file => {
        const length = parseInt(file.Length, 10)
        return !isNaN(length) && length > 0
      }).map(file => {
        return {
          type: 'file',
          id: file.UniqueId,
          name: file.Name,
          title: file.Title,
          created: file.TimeCreated,
          modified: file.TimeLastModified,
          uri: absoluteUrl(scRoot, file.ServerRelativeUrl),
          serverRelativeUrl: file.ServerRelativeUrl
        }
      })
      dispatch({ type: FILES_GOT_SEARCH_RESULTS_ACTION, results })
    }

    function onEnd () {
      if (requestId !== latestFileSearchRequest) {
        return
      }
      dispatch({ type: FILES_END_SEARCH_ACTION })
    }

    try {
      const spToken = await getSpAccessToken(userData)
      await searchDocumentLibrary({
        siteUrl,
        serverRelativeUrl,
        query,
        onResults,
        onEnd
      }, spToken)
    } catch (err) {
      if (requestId !== latestFileSearchRequest) {
        return
      }
      return dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_FILE_SEARCH,
        errorMessage: err.message
      })
    }
  }
}

/**
 * Returns server relative url to a document folder for a particular item
 */
function getServerRelativeUrl (siteItem) {
  switch (siteItem.type) {
    case 'contact':
    case 'project':
    case 'department':
      return `${(new URL(siteItem.url)).pathname}/files`
    case 'category':
      return `${(new URL(siteItem.collection_url)).pathname}/files_${siteItem.id}`
    case 'templates':
      return `${(new URL(siteItem.url)).pathname}/pdsMallar`
  }
  return ''
}

function documentLibraryError (err) {
  console.error('documentLibraryerror', err)
  // TODO if error code is 401, then reload page
  return {
    type: ERROR_ACTION,
    errorType: ERROR_TYPE_DOC_LIB_GET,
    errorMessage: err.message
  }
}

function findTreeFolderByPath (items, targetPath) {
  if (!Array.isArray(items) || items.length === 0) {
    return null
  }

  const normalizedTarget = normalizePath(targetPath)
  const queue = [...items]

  while (queue.length > 0) {
    const current = queue.shift()
    if ((current?.type === 'folder' || current?.type === 'rootfolder') && normalizePath(current.path) === normalizedTarget) {
      return current
    }

    if (Array.isArray(current?.children) && current.children.length > 0) {
      queue.push(...current.children)
    }
  }

  return null
}

function normalizePath (path) {
  return String(path || '').replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase()
}

function compareFilenames (_lhs, _rhs) {
  const lhs = _lhs.name.toLowerCase()
  const rhs = _rhs.name.toLowerCase()
  if (lhs < rhs) return -1
  if (lhs === rhs) return 0
  if (lhs > rhs) return 1
}

function getPdfAttachmentName (name) {
  const fileName = String(name || '').trim()
  const ext = getFileExtension(fileName)
  if (!fileName.length || !ext.length) {
    return `${fileName || 'attachment'}.pdf`
  }

  const suffix = `.${ext}`
  return fileName.toLowerCase().endsWith(suffix)
    ? `${fileName.slice(0, -suffix.length)}.pdf`
    : `${fileName}.pdf`
}

function buildRenameLockHint (lockDiagnostics, strings) {
  const get = (key, fallback) => {
    const value = strings?.[key]
    return typeof value === 'string' && value.length > 0 ? value : fallback
  }

  const genericHint = get(
    'RENAME_LOCK_HINT_GENERIC',
    'The file or folder is in use. Close it in Word/Outlook and try again in a moment.'
  )

  if (!lockDiagnostics || typeof lockDiagnostics !== 'object') {
    return genericHint
  }

  const { lockedByUser, lockedFiles } = lockDiagnostics

  if (lockedByUser && (lockedByUser.Email || lockedByUser.Title)) {
    const who = lockedByUser.Email || lockedByUser.Title
    return formatResourceString(
      get(
        'RENAME_LOCK_HINT_LOCKED_BY_USER',
        'Locked by %1. Ask that user to close the document, then try again.'
      ),
      who
    )
  }

  if (Array.isArray(lockedFiles) && lockedFiles.length > 0) {
    const firstLockedFile = lockedFiles.find((file) => file?.lockedByUser && (file.lockedByUser.Email || file.lockedByUser.Title))
    if (firstLockedFile) {
      const who = firstLockedFile.lockedByUser.Email || firstLockedFile.lockedByUser.Title
      return formatResourceString(
        get(
          'RENAME_LOCK_HINT_FOLDER_LOCKED_BY_USER',
          'A file in this folder is locked by %1 (%2). Ask that user to close it, then try again.'
        ),
        who,
        firstLockedFile.name
      )
    }

    return formatResourceString(
      get(
        'RENAME_LOCK_HINT_FOLDER_FILE_IN_USE',
        'A file in this folder is in use (%1). Close it and try again.'
      ),
      lockedFiles[0].name
    )
  }

  return genericHint
}

function buildDeleteLockHint (lockDiagnostics, strings) {
  const get = (key, fallback) => {
    const value = strings?.[key]
    return typeof value === 'string' && value.length > 0 ? value : fallback
  }

  const lockedFiles = Array.isArray(lockDiagnostics?.lockedFiles) ? lockDiagnostics.lockedFiles : []
  const itemCount = Number(lockDiagnostics?.itemCount)

  if (lockedFiles.length > 0) {
    const lockedByUserFile = lockedFiles.find((file) => {
      const lockedBy = file?.lockedByUser
      const checkedOutBy = file?.checkOutByUser
      return !!((lockedBy && (lockedBy.Email || lockedBy.Title)) ||
        (checkedOutBy && (checkedOutBy.Email || checkedOutBy.Title)))
    })

    if (lockedByUserFile) {
      const lockedBy = lockedByUserFile.lockedByUser || lockedByUserFile.checkOutByUser || {}
      const who = lockedBy.Email || lockedBy.Title || 'another user'
      return formatResourceString(
        get(
          'DELETE_LOCK_HINT_FOLDER_LOCKED_BY_USER',
          'Cannot delete this folder because "%2" is currently open by %1. Ask that user to close the file and try again.'
        ),
        who,
        lockedByUserFile.name || 'a file'
      )
    }

    return formatResourceString(
      get(
        'DELETE_LOCK_HINT_FOLDER_FILE_IN_USE',
        'Cannot delete this folder because "%1" is currently in use. Close the file and try again.'
      ),
      lockedFiles[0]?.name || 'a file'
    )
  }

  if (Number.isFinite(itemCount) && itemCount > 0) {
    return formatResourceString(
      get(
        'DELETE_HINT_FOLDER_NOT_EMPTY_WITH_COUNT',
        'Cannot delete this folder right now. Try again in a moment.'
      ),
      itemCount
    )
  }

  return get(
    'DELETE_HINT_FOLDER_NOT_EMPTY',
    'Cannot delete this folder right now. Try again in a moment.'
  )
}

async function refreshFileViewAfterMutation (dispatch, siteItem, mutatedItem) {
  const { files } = store.getState()
  const { showSearchResults, searchValue, selectedFileNode, trees } = files
  const query = String(searchValue || '').trim()

  if (showSearchResults && query.length > 0) {
    await dispatch(searchFiles(siteItem, query))
  }

  const treeId = `${siteItem.type}:${siteItem.id}`
  const treeItems = trees?.[treeId]?.items || []
  const targetFolderPath = getMutationParentFolderPath(mutatedItem)

  if (targetFolderPath) {
    const targetFolder = findTreeFolderByPath(treeItems, targetFolderPath)
    if (targetFolder) {
      if (targetFolder.type === 'rootfolder') {
        await dispatch(getRootFolder(siteItem))
      } else {
        await dispatch(getSubFolder(siteItem, targetFolder))
      }
      return
    }
  }

  if (selectedFileNode?.type === 'folder' && selectedFileNode.id !== mutatedItem?.id) {
    await dispatch(getSubFolder(siteItem, selectedFileNode))
  } else {
    await dispatch(getRootFolder(siteItem))
  }
}

function getMutationParentFolderPath (item) {
  if (!item || typeof item.path !== 'string') {
    return ''
  }

  if (item.type === 'file') {
    return item.path
  }

  if (item.type === 'folder') {
    const segments = String(item.path || '').split('/').filter(Boolean)
    if (segments.length <= 1) {
      return ''
    }
    return segments.slice(0, -1).join('/')
  }

  return ''
}
